import React from 'react';
import { useSearchParams } from 'react-router-dom';

import EventDetailsStore from 'stores/EventDetails';
import { AmendmentSummaryType, SubmissionVersionType } from 'utils/api/modules/entities/entities.types';

import { parseVersionParam, setVersionParam } from './versionParam';

export type VersionHistoryState = {
  loading: boolean;
  error: boolean;
  versions?: SubmissionVersionType[];
  summary: AmendmentSummaryType | null;
  displayed: SubmissionVersionType | null;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelect: (entry: SubmissionVersionType) => void;
  onReturnToLatest: () => void;
  onRetry: () => void;
};

/**
 * Which panel is open, which version is selected, and the
 * URL parameter that makes the selection shareable.
 */
export const useVersionHistory = (
  store: EventDetailsStore | null,
  eventUuid: string | undefined,
  summary: AmendmentSummaryType | null,
  versions: SubmissionVersionType[] | undefined,
  loading: boolean,
  error: boolean
): VersionHistoryState => {
  const [open, setOpen] = React.useState(false);
  const [selectedUuid, setSelectedUuid] = React.useState<string | null>(null);
  const [attempt, setAttempt] = React.useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedVersion = parseVersionParam(searchParams);

  const displayed = versions?.find((entry) => entry.uuid === selectedUuid) ?? null;

  const versionsRef = React.useRef(versions);
  versionsRef.current = versions;

  const fetchedAttemptRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!store || !eventUuid) return;
    if (!open && requestedVersion === null) return;
    if (versionsRef.current && fetchedAttemptRef.current === attempt) return;

    fetchedAttemptRef.current = attempt;
    store.loadSubmissionVersions(eventUuid);
  }, [store, eventUuid, open, requestedVersion, attempt]);

  const urlVersionConsumed = React.useRef(false);

  React.useEffect(() => {
    if (requestedVersion === null || !versions || selectedUuid !== null || urlVersionConsumed.current) return;
    const match = versions.find((entry) => entry.version === requestedVersion && !entry.isCurrent);
    // A stale link falls back to the latest.
    if (!match) return;
    urlVersionConsumed.current = true;
    setSelectedUuid(match.uuid);
  }, [requestedVersion, versions, selectedUuid]);

  const writeVersion = (version: number | null): void =>
    setSearchParams((current) => setVersionParam(current, version), { replace: true });

  return {
    loading,
    error,
    versions,
    summary,
    displayed,
    open,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    onSelect: (entry: SubmissionVersionType): void => {
      setSelectedUuid(entry.isCurrent ? null : entry.uuid);
      writeVersion(entry.isCurrent ? null : entry.version);
      setOpen(false);
    },
    onReturnToLatest: (): void => {
      setSelectedUuid(null);
      writeVersion(null);
    },
    onRetry: () => setAttempt((n) => n + 1),
  };
};
